import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import Button from '../../components/atoms/forms/Button';
import Badge from '../../components/atoms/Badge';
import {
  parseSchedulePayload,
  formatScheduleDateTime,
} from '../SchemaTemplates/SchemaTemplatesUtils';
import styles from './ScheduledChangeModal.module.css';

function payloadRows(payload) {
  const parsed = parseSchedulePayload(payload);
  if (!parsed) return null;
  const rows = [['Template type', parsed.templateType || '—']];
  if (parsed.discount) {
    rows.push([
      'Discount',
      `${parsed.discount.discount}% ${parsed.discount.priceDisplayType || ''}${
        parsed.discount.platform ? ` · ${parsed.discount.platform}` : ''
      }`,
    ]);
  }
  if (parsed.subsidy) {
    rows.push([
      'Subsidy',
      `${parsed.subsidy.discount}% ${parsed.subsidy.priceDisplayType || ''}`,
    ]);
  }
  if (parsed.expiring) {
    rows.push([
      'Expiring',
      `${parsed.expiring.expiringDays}d @ ${parsed.expiring.discount}% ${
        parsed.expiring.priceDisplayType || ''
      }`,
    ]);
  }
  if (parsed.sharedSubsidy) {
    rows.push([
      'Shared subsidy',
      `target ${parsed.sharedSubsidy.targetDiscount}% · client ${
        parsed.sharedSubsidy.clientSubsidy
      }% ${parsed.sharedSubsidy.priceDisplayType || ''}`,
    ]);
  }
  if (parsed.timeBound) {
    const windows = (parsed.timeBound.timeWindows || [])
      .map((w) => `${w.dayOfWeek} ${w.startTime}-${w.endTime}`)
      .join(', ');
    rows.push([
      'Time-bound',
      `${parsed.timeBound.discount}% ${
        parsed.timeBound.priceDisplayType || ''
      } — ${windows}`,
    ]);
  }
  if (parsed.sleepScreenId) rows.push(['Sleep screen', parsed.sleepScreenId]);
  return rows;
}

const ScheduledChangeModal = ({
  schemaId,
  rows,
  onClose,
  onCancelRow,
  cancellingKey,
  cancelError,
}) => (
  <Modal show onHide={onClose}>
    <Modal.Header closeButton>
      <Modal.Title>Scheduled change — {schemaId}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {cancelError && <p className={styles.error}>{cancelError}</p>}
      {rows.length === 0 ? (
        <p className={styles.muted}>
          No pending scheduled change for this schema.
        </p>
      ) : null}
      {rows.map((row) => {
        const key = `${row.jobId}-${row.schemaId}`;
        const details = payloadRows(row.payload);
        return (
          <div className={styles.change} key={key}>
            <div className={styles.metaRow}>
              <span className={styles.goLive}>
                Goes live {formatScheduleDateTime(row.actionTime)}
              </span>
              <Badge variant="scheduled">{row.status}</Badge>
            </div>
            {row.scheduledByEmail && (
              <p className={styles.muted}>
                Scheduled by {row.scheduledByEmail}
              </p>
            )}
            {details ? (
              <dl className={styles.details}>
                {details.map(([k, v]) => (
                  <div className={styles.detailRow} key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className={styles.muted}>Could not read change details.</p>
            )}
            <Button
              variant="danger-outline"
              onClick={() => onCancelRow(row.jobId, row.schemaId)}
              disabled={cancellingKey === key}
            >
              {cancellingKey === key
                ? 'Cancelling…'
                : "Cancel this fridge's change"}
            </Button>
          </div>
        );
      })}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="primary" onClick={onClose}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

ScheduledChangeModal.propTypes = {
  schemaId: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onCancelRow: PropTypes.func.isRequired,
  cancellingKey: PropTypes.string,
  cancelError: PropTypes.string,
};

ScheduledChangeModal.defaultProps = {
  cancellingKey: null,
  cancelError: null,
};

export default ScheduledChangeModal;
